import React, { useEffect, useMemo, useState } from "react";
import Swal from "sweetalert2";
import { AppAPIService } from "../../../../services/AppAPIService";
import styles from "./styles.module.scss";

const formatDate = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatWaitingTime = (minutes) => {
  if (minutes === null || minutes === undefined) return "-";
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest ? `${hours}h ${rest}min` : `${hours}h`;
};

const AssistantHandoffsPage = () => {
  window.setPageTitle("Atendimentos do Assistente");

  const api = useMemo(() => AppAPIService.getInstance(), []);
  const [handoffs, setHandoffs] = useState([]);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState("pending");

  const fetchHandoffs = async (nextStatus = statusFilter) => {
    setLoading(true);
    try {
      const data = await api.makeHttpRequest({
        method: "GET",
        url: `/assistant/handoffs?status=${nextStatus}`,
      });
      setHandoffs(Array.isArray(data?.items) ? data.items : []);
      setMessage(data?.message || "");
    } catch (error) {
      setMessage("Nao foi possivel carregar os pedidos de atendimento humano agora.");
    } finally {
      setLoading(false);
    }
  };

  const handleResolveHandoff = async (item) => {
    const result = await Swal.fire({
      title: "Marcar como tratado?",
      text: "Registre o que foi conversado ou combinado com o cliente antes de fechar.",
      icon: "question",
      input: "textarea",
      inputPlaceholder: "Ex.: Cliente orientado por telefone a enviar o contrato e o comprovante de endereço.",
      inputAttributes: {
        "aria-label": "Resumo do atendimento",
      },
      showCancelButton: true,
      confirmButtonText: "Sim, marcar",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#9a5148",
      inputValidator: (value) => {
        if (!String(value || "").trim()) {
          return "Informe um breve resumo do atendimento para manter o histórico.";
        }
        if (String(value || "").trim().length < 8) {
          return "Descreva um pouco melhor o que foi tratado.";
        }
        return undefined;
      },
    });

    if (!result.isConfirmed) return;

    try {
      const data = await api.makeHttpRequest({
        method: "PATCH",
        url: `/assistant/handoffs/${item.id}/resolve`,
        data: {
          resolutionNote: String(result.value || "").trim(),
        },
      });

      if (!data?.success) {
        Swal.fire("Atenção", data?.message || "Pedido não foi atualizado.", "warning");
        return;
      }

      setHandoffs((current) => current.filter((handoff) => String(handoff.id) !== String(item.id)));
      setMessage(data?.message || "Solicitação marcada como tratada.");
      fetchHandoffs(statusFilter);
      Swal.fire("Pronto", "Pedido marcado como tratado.", "success");
    } catch (error) {
      Swal.fire("Erro", "Não foi possível marcar o pedido como tratado agora.", "error");
    }
  };

  const handleResolveAllPending = async () => {
    const pendingItems = handoffs.filter((item) => String(item?.status || "") !== "HANDOFF_RESOLVED");

    const result = await Swal.fire({
      title: "Limpar pendentes?",
      text: "Os pedidos pendentes serão marcados como tratados, mantendo histórico em Tratados.",
      icon: "question",
      input: "textarea",
      inputValue: "Limpeza operacional da fila após revisão dos testes e pedidos antigos.",
      inputPlaceholder: "Informe o motivo da limpeza da fila.",
      inputAttributes: {
        "aria-label": "Motivo da limpeza",
      },
      showCancelButton: true,
      confirmButtonText: "Limpar fila",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#9a5148",
      inputValidator: (value) => {
        if (!String(value || "").trim()) {
          return "Informe uma observação para manter o histórico.";
        }
        if (String(value || "").trim().length < 8) {
          return "Descreva um pouco melhor o motivo da limpeza.";
        }
        return undefined;
      },
    });

    if (!result.isConfirmed) return;

    const resolutionNote = String(result.value || "").trim();

    setLoading(true);
    Swal.fire({
      title: "Limpando pendentes...",
      text: "Os pedidos estão sendo marcados como tratados.",
      allowOutsideClick: false,
      allowEscapeKey: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });

    try {
      let data;

      try {
        data = await api.makeHttpRequest({
          method: "PATCH",
          url: "/assistant/handoffs/resolve-pending",
          data: {
            resolutionNote,
          },
        });
      } catch (error) {
        // Older API deployments may not have the bulk cleanup route yet.
      }

      if (!data?.success) {
        let resolved = 0;
        let failed = 0;

        for (const item of pendingItems) {
          try {
            const itemResult = await api.makeHttpRequest({
              method: "PATCH",
              url: `/assistant/handoffs/${item.id}/resolve`,
              data: {
                resolutionNote,
              },
            });

            if (itemResult?.success) {
              resolved += 1;
            } else {
              failed += 1;
            }
          } catch (error) {
            failed += 1;
          }
        }

        data = {
          success: resolved > 0,
          resolved,
          failed,
          message: failed
            ? `${resolved} pedido(s) marcado(s) como tratado(s). ${failed} nao puderam ser atualizados.`
            : `${resolved} pedido(s) marcado(s) como tratado(s).`,
        };
      }

      if (!data?.success) {
        Swal.fire("Atenção", data?.message || "A fila não foi atualizada.", "warning");
        return;
      }

      setHandoffs([]);
      setMessage(data?.message || "Fila de pendentes limpa.");
      await fetchHandoffs("pending");
      Swal.fire("Pronto", data?.message || "Pedidos pendentes marcados como tratados.", "success");
    } catch (error) {
      Swal.fire("Erro", "Não foi possível limpar a fila agora.", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHandoffs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className={styles.container}>
      <section className={styles.header}>
        <h1>Atendimentos do Assistente</h1>
        <p>Pedidos recentes em que o cliente solicitou atendimento humano pelo chat.</p>
      </section>

      <section className={styles.summary}>
        <div>
          <strong>
            {loading
              ? "Carregando..."
              : `${handoffs.length} ${statusFilter === "resolved" ? "tratado(s)" : "pedido(s) recente(s)"}`}
          </strong>
          <span>{message || "Fila inicial do handoff cognitivo."}</span>
        </div>
        <div className={styles.links}>
          <button type="button" onClick={() => fetchHandoffs(statusFilter)} disabled={loading}>
            Atualizar
          </button>
          {statusFilter === "pending" && handoffs.length > 0 && (
            <button type="button" className={styles.dangerButton} onClick={handleResolveAllPending} disabled={loading}>
              Limpar pendentes
            </button>
          )}
          <a href="/admin/assistente">Assistente</a>
        </div>
      </section>

      <section className={styles.handoffFilters}>
        <button
          type="button"
          className={statusFilter === "pending" ? styles.activeFilter : ""}
          onClick={() => {
            setStatusFilter("pending");
            fetchHandoffs("pending");
          }}
          disabled={loading}
        >
          Pendentes
        </button>
        <button
          type="button"
          className={statusFilter === "resolved" ? styles.activeFilter : ""}
          onClick={() => {
            setStatusFilter("resolved");
            fetchHandoffs("resolved");
          }}
          disabled={loading}
        >
          Tratados
        </button>
      </section>

      <section className={styles.handoffList}>
        {handoffs.length === 0 && (
          <div className={styles.emptyState}>
            {loading ? "Carregando pedidos..." : "Nao ha pedidos recentes de atendimento humano."}
          </div>
        )}

        {handoffs.map((item) => (
          <article className={styles.handoffCard} key={item.id}>
            <div className={styles.handoffHeader}>
              <div>
                <strong>{item.user?.name || "Cliente nao identificado"}</strong>
                <span>{item.user?.email || item.user?.cpfCnpj || "Sem contato cadastrado"}</span>
              </div>
              <div className={styles.handoffMeta}>
                <span className={item.priority === "ALTA" ? styles.priorityHigh : styles.priorityNormal}>
                  {item.priority || "NORMAL"}
                </span>
                <time>{formatDate(item.createdAt)}</time>
                <small>
                  {item.status === "HANDOFF_RESOLVED"
                    ? `tratado em ${formatDate(item.resolution?.resolvedAt)}`
                    : `aguardando ${formatWaitingTime(item.waitingMinutes)}`}
                </small>
              </div>
            </div>

            <div className={styles.handoffDetails}>
              <span>
                <strong>Processo:</strong>{" "}
                {item.lawsuit?.courtCaseNumber || item.lawsuit?.id || "Nao informado"}
              </span>
              <span>
                <strong>Parte adversa:</strong> {item.lawsuit?.adverseParty || "Nao informada"}
              </span>
              <span>
                <strong>Telefone:</strong> {item.user?.phone || "Nao informado"}
              </span>
              <span>
                <strong>Data limite:</strong> {formatDate(item.lawsuit?.limitDate).replace(", 00:00", "")}
              </span>
              <span>
                <strong>CPF/CNPJ:</strong> {item.user?.cpfCnpj || "Nao informado"}
              </span>
              <span>
                <strong>Origem:</strong> {item.scope === "PROCESS" ? "Processo" : "Global"}
              </span>
            </div>

            <div className={styles.handoffQuestion}>
              <strong>Pedido do cliente</strong>
              <p>{item.question || "Cliente solicitou atendimento humano pelo assistente."}</p>
            </div>

            {item.resolution && (
              <div className={styles.handoffResolution}>
                <strong>Tratativa registrada</strong>
                <p>{item.resolution.note || "Tratativa marcada sem observação detalhada."}</p>
                <small>
                  {item.resolution.resolvedBy?.name || item.resolution.resolvedBy?.email || "Equipe"} em{" "}
                  {formatDate(item.resolution.resolvedAt)}
                </small>
              </div>
            )}

            <div className={styles.handoffActions}>
              {item.lawsuit?.id && <a href={`/admin/processo/${item.lawsuit.id}/editar`}>Abrir processo</a>}
              {item.user?.phone && <a href={`tel:${item.user.phone}`}>Ligar</a>}
              {item.user?.email && <a href={`mailto:${item.user.email}`}>Enviar e-mail</a>}
              {item.status !== "HANDOFF_RESOLVED" && (
                <button type="button" onClick={() => handleResolveHandoff(item)}>
                  Marcar como tratado
                </button>
              )}
            </div>
          </article>
        ))}
      </section>
    </div>
  );
};

export default AssistantHandoffsPage;
